import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  const movie = getMovieById(id || '');

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">影片未找到</h1>
            <p className="text-foreground-500 mb-4">该影片可能已下架或链接无效</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedMovies = getRelatedMovies(movie);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    url: `https://www.97cinema.com/article/${movie.id}`,
    image: movie.posterImage,
    dateCreated: movie.releaseDate,
    description: movie.description,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map(name => ({
      '@type': 'Person',
      name,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: 10,
      ratingCount: Math.floor(movie.viewCount / 100),
    },
    countryOfOrigin: {
      '@type': 'Country',
      name: movie.region,
    },
    genre: movie.tags,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: '影视库', href: '/category/all' },
                    { label: movie.category, href: `/category/${movie.categorySlug}` },
                    { label: movie.title },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="w-full h-[300px] md:h-[420px] relative overflow-hidden">
            <img
              src={movie.bannerImage}
              alt={movie.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>
          </div>

          <div className="w-full px-4 md:px-6 -mt-32 relative z-10">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row gap-6 md:gap-10">
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="w-44 md:w-56 rounded-lg overflow-hidden shadow-xl">
                    <img
                      src={movie.posterImage}
                      alt={movie.title}
                      className="w-full aspect-[2/3] object-cover object-top"
                      title={`${movie.title} - 97影院高清在线观看`}
                    />
                  </div>
                </div>

                <div className="flex-1 pt-2 md:pt-8">
                  <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-2">
                    {movie.title}
                  </h1>
                  <p className="text-sm text-foreground-500 mb-4">{movie.englishTitle}</p>

                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {movie.tags.map(tag => (
                      <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">评分</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500"></i>
                        <span className="text-sm font-semibold text-foreground-900">{movie.rating}</span>
                        <span className="text-xs text-foreground-400">/ 10</span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">年份</span>
                      <span className="text-sm text-foreground-900">{movie.year}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">类型</span>
                      <span className="text-sm text-foreground-900">{movie.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">地区</span>
                      <span className="text-sm text-foreground-900">{movie.region}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">时长</span>
                      <span className="text-sm text-foreground-900">{movie.duration}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">状态</span>
                      <span className={`text-sm font-medium ${movie.updateStatus === '即将上映' ? 'text-accent-600' : 'text-primary-600'}`}>
                        {movie.updateStatus}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">导演</span>
                      <span className="text-sm text-foreground-900">{movie.director}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">观看</span>
                      <span className="text-sm text-foreground-900">{(movie.viewCount / 10000).toFixed(0)}万次</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-play-circle-fill text-lg"></i>
                      立即播放
                    </button>
                    <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-200 text-foreground-700 font-medium hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                    <button className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-background-200 text-foreground-700 font-medium hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-share-forward-line"></i>
                      分享
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                <div className="flex-1">
                  <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">剧情简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {movie.description}
                  </p>

                  <h3 className="font-heading text-base font-bold text-foreground-900 mt-8 mb-3">主演阵容</h3>
                  <div className="flex flex-wrap gap-2">
                    {movie.cast.map(actor => (
                      <span key={actor} className="px-3 py-1.5 text-sm rounded-lg bg-background-100 text-foreground-700 whitespace-nowrap">
                        {actor}
                      </span>
                    ))}
                  </div>

                  <div className="mt-8 p-5 rounded-lg bg-primary-50 border border-primary-100">
                    <h3 className="font-heading text-base font-bold text-primary-700 mb-2">97影院观影提示</h3>
                    <p className="text-sm text-primary-600/80">
                      本站提供{movie.title}高清在线观看，超清蓝光画质，不缓冲流畅播放。所有内容均来自互联网，仅供学习交流使用，请于下载后24小时内删除。如您喜欢本片，建议购买正版。
                    </p>
                  </div>
                </div>

                <div className="lg:w-80 flex-shrink-0">
                  <div className="sticky top-24">
                    <h3 className="font-heading text-base font-bold text-foreground-900 mb-4">相关推荐</h3>
                    <div className="space-y-3">
                      {relatedMovies.map(rm => (
                        <Link
                          key={rm.id}
                          to={`/article/${rm.id}`}
                          className="flex gap-3 p-2.5 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                        >
                          <div className="w-16 h-22 flex-shrink-0 rounded-md overflow-hidden">
                            <img src={rm.posterImage} alt={rm.title} className="w-full h-full object-cover object-top" title={`${rm.title} - 97影院高清在线观看`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">{rm.title}</h4>
                            <p className="text-xs text-foreground-500 mt-1">{rm.category} · {rm.year}</p>
                            <div className="flex items-center gap-1 mt-1">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                              <span className="text-xs text-foreground-600">{rm.rating}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-6">猜你喜欢</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.slice(0, 6).map(rm => (
                  <MovieCard key={rm.id} movie={rm} />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}